import useCorporation from "@/composables/useCorporation";
import useCorporationsSearch from "@/composables/useCorporationsSearch";
import { Corporation } from "@/interfaces/corporation";
import { useVuelidate } from "@vuelidate/core";
import { minLength, required } from "@vuelidate/validators";
import { computed, reactive, ref, watch } from "vue";
import { useToast } from "vue-toastification";

const useCorporationForm = ({ immediate = false }: { immediate: boolean }) => {
  const toast = useToast();

  const {
    corporations: CorporationsCoincidences,
    setQuery,
    refetch,
    query: searchQuery,
  } = useCorporationsSearch({ immediate });
  const { createMutation: createCorporation } = useCorporation();

  const isCreating = ref(false);

  const form = reactive({
    name: "",
  });

  const formRules = {
    name: {
      required,
      minLength: minLength(3),
    },
  };
  const formVuelidate = useVuelidate(formRules, form);

  const confirming = ref(false);
  const corporation = ref<Corporation | null>(null);

  const duplicatedCorporation = computed(() => {
    return CorporationsCoincidences.value.filter(
      (corporation) =>
        corporation.name.toLowerCase() === form.name.toLowerCase(),
    );
  });

  const handleEvaluation = async () => {
    try {
      isCreating.value = true;
      setQuery(form.name);
      await refetch();
      const validation = await formVuelidate.value.$validate();

      if (validation && !duplicatedCorporation.value.length) {
        if (CorporationsCoincidences.value.length) {
          confirming.value = true;
        } else {
          const data = await handleCreate();

          return data;
        }
      } else {
        isCreating.value = false;
        if (duplicatedCorporation.value.length) {
          toast.error(`Ya existe un grupo con el nombre "${form.name}".`);
          return;
        }

        toast.error("El corporativo no puede tener ese nombre");
        return;
      }
    } catch (err) {
      // Error occurred
    }
  };

  const resetForm = () => {
    // Reset form
    form.name = "";
  };

  const handleCreate = async (raiseToast: boolean = true) => {
    try {
      isCreating.value = true;
      const data = await createCorporation.mutateAsync(form.name);
      await refetch();
      raiseToast && toast.success(`Corporativo creado correctamente.`);
      resetForm();
      return data;
    } catch (err) {
      // Error occurred
    } finally {
      isCreating.value = false;
      confirming.value = false;
    }
  };

  watch(
    form,
    async () => {
      await formVuelidate.value.$validate();
    },
    { immediate: true, deep: true },
  );

  watch(form, (value) => {
    // Form submitted
  });

  return {
    searchQuery,
    corporation,
    form,
    formVuelidate,
    duplicatedCorporation,
    confirming,
    isCreating,
    CorporationsCoincidences,
    setQuery,
    handleEvaluation,
    handleCreate,
    refetch,
  };
};

export default useCorporationForm;
